import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Meta, useLazyPatientMetaQuery } from '../../../graphql/graphql';
import { useEffectAsync } from '../../../helpers';
import {
	selectPatientDiagnosis,
	selectPatientMetaIsLoading,
	setPatientDiagnosis,
	setPatientMetaIsLoading,
} from '../../../store/reducers/patientMetaSlice';
import { DocumentContentSubType } from '../../../Modules/Documents/Enums/documentContentSubType.enum';
import { MetaKey } from '../../../Modules/Meta/Enums/MetaKey.enum';
import { useAppContext } from '../../../Services/AppContext';

interface Diagnosis {
	label: string;
	value: string;
	type: string;
}
interface PatientDiagnoses {
	mainDiagnosis: Diagnosis;
	secondaryDiagnoses: Diagnosis[];
}

export default function usePatientMeta(patientId: string, meta?: Meta[]) {
	const [getPatientMeta] = useLazyPatientMetaQuery();
	const [patientMeta, setPatientMeta] = useState<any>();
	const [isLoading, setIsLoading] = useState(false);
	const dispatch = useDispatch();
	const { workplaceId } = useAppContext();

	useEffectAsync(async () => {
		await handleLoadMeta();
	}, []);

	const handleLoadMeta = async (force?: boolean) => {
		setIsLoading(true);
		dispatch(setPatientMetaIsLoading(true));
		const data = force
			? await getPatientMeta({ id: patientId }).unwrap()
			: meta
			? { user: { meta: meta } }
			: await getPatientMeta({ id: patientId }).unwrap();
		if (data) {
			handleProcessPatientDiagnosis(data);
			setPatientMeta({ data: data });
		}
		dispatch(setPatientMetaIsLoading(false));
		setIsLoading(false);
	};

	const handleProcessPatientDiagnosis = (data: any) => {
		const diagnoses = data.user?.meta?.find(
			(item: any) => item?.key === 'diagnosis',
		)?.value;

		const finalData = {
			mainDiagnosis: diagnoses?.find(
				(item: any) => item?.subType === DocumentContentSubType.MAIN_DIAGNOSIS,
			),
			secondaryDiagnoses: diagnoses?.filter(
				(item: any) =>
					item?.subType === DocumentContentSubType.SECONDARY_DIAGNOSIS,
			),
		};
		dispatch(setPatientDiagnosis(finalData));
	};

	const personActingOnBehalfOfAPatient = useMemo(() => {
		return patientMeta?.data?.user?.meta?.find(
			(item: any) => item?.key === 'personActingOnBehalfOfAPatient',
		)?.value;
	}, [patientMeta]);

	const getPersonActingOnBehalfOfAPatient = () => {
		let text = '';
		if (personActingOnBehalfOfAPatient?.fullName) {
			text += personActingOnBehalfOfAPatient.fullName;
		}
		if (personActingOnBehalfOfAPatient?.relationship) {
			text += ' (' + personActingOnBehalfOfAPatient.relationship + ')';
		}
		return text;
	};

	const identificationCode = useMemo(() => {
		return patientMeta?.data?.user?.meta?.find(
			(item: any) => item?.key === 'identificationCode',
		)?.value;
	}, [patientMeta]);

	const canCreateCaseOnPermanentMedication = useMemo(() => {
		return patientMeta?.data?.user?.meta?.find(
			(item: any) =>
				item?.key === MetaKey.CAN_CREATE_CASE_ON_PERMANENT_MEDICATION &&
				item?.workplaceId === workplaceId,
		)?.value;
	}, [patientMeta]);

	return {
		personActingOnBehalfOfAPatient,
		hasPersonActingOnBehalfOfAPatient: !!personActingOnBehalfOfAPatient,
		getPersonActingOnBehalfOfAPatient,
		reFetchMeta: () => handleLoadMeta(true),
		metaIsLoading: useSelector(selectPatientMetaIsLoading) || isLoading,
		patientDiagnosis: useSelector(selectPatientDiagnosis),
		patientMeta: patientMeta?.data?.user?.meta as Meta[],
		identificationCode,
		canCreateCaseOnPermanentMedication,
	};
}
